import { Component, inject, OnInit } from '@angular/core';
import { CrmActionButton } from 'common-module/buttons';
import { CrmMessageService } from 'common-module/message';
import { CrmSelectOption } from 'common-module/select';
import { NgIf } from '@angular/common';
import { PlCardProductInputComponent } from '@app/shared/components/pl-card-product-input/pl-card-product-input.component';
import { CrmTranslatePipe } from 'common-module/translate';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { PlCardUploadComponent } from '@app/shared/components/pl-card-upload/pl-card-upload.component';
import { CrmDisabledDirectiveModule, CrmResolveExpressionPipe } from 'common-module/core';
import { ModalFooterComponent } from '@app/shared/components/footer/modal-footer.component';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';

export enum FooterActionId {
  confirm = 'modal_footer_confirm',
  cancel = 'modal_footer_cancel'
}

@Component({
  selector: 'app-plcard-new-modal',
  template: `
    <div class="new-pl-card-modal">
      <ng-container *ngIf="multiProduct">
        <app-plcard-product-input
          [btnLabel]="'generic.addBtnLabel' | crmTranslate"
          [label]="'modal.plCard.new.productCodes.label' | crmTranslate"
          [placeholder]="'modal.plCard.new.productCodes.placeholder' | crmTranslate"
          (tagsChanged)="onTagsChanged($event)"
        ></app-plcard-product-input>
        <nz-divider></nz-divider>
      </ng-container>
      <app-pl-card-upload
        class="upload"
        crmDisabled
        [isDisabled]="uploadDisabled | crmResolveExpression: false"
        [disabledTooltip]="'modal.plCard.new.message.selectProductFirst' | crmTranslate"
        [label]="'modal.plCard.new.cardFile.label' | crmTranslate"
        [placeholder]="'modal.plCard.new.cardFile.placeholder' | crmTranslate"
        (fileChanged)="handleFileChanged($event)"
      ></app-pl-card-upload>
      <app-modal-footer
        [actions]="headerActions"
        (actionClicked)="handleFooterAction($event)"
      ></app-modal-footer>
    </div>
  `,
  styles: [
    `
      .new-pl-card-modal .ant-divider-horizontal {
        margin: 30px 0;
      }
    `
  ],
  imports: [
    NgIf,
    PlCardProductInputComponent,
    CrmTranslatePipe,
    NzDividerModule,
    PlCardUploadComponent,
    CrmDisabledDirectiveModule,
    CrmResolveExpressionPipe,
    ModalFooterComponent
  ],
  standalone: true
})
export class PlcardCreateModalComponent implements OnInit {
  public multiProduct = true;
  public file?: File;
  public existingCard?: string;
  public onCancel?: () => void;
  public onSubmit?: (inst: PlcardCreateModalComponent) => void;
  public productCodes: Pick<CrmSelectOption, 'value'>[] = [];

  public headerActions: CrmActionButton[] = [
    {
      title: 'modal.footer.confirm',
      type: 'primary',
      id: FooterActionId.confirm,
      isDisabled: () => !this.isFormValid(),
      disabledTooltip: 'modal.plCard.new.message.submit'
    },
    {
      title: 'modal.footer.cancel',
      id: FooterActionId.cancel,
      className: 'ant-btn-secondary'
    }
  ];

  modalData = inject(NZ_MODAL_DATA);

  constructor(private messageService: CrmMessageService) {}

  ngOnInit() {
    const { multiProduct, onCancel, onSubmit } = this.modalData;

    this.onCancel = onCancel;
    this.onSubmit = onSubmit;
    this.multiProduct = multiProduct;
  }

  public uploadDisabled = () => this.multiProduct && !this.productCodes?.length;

  public onTagsChanged(tags: CrmSelectOption[]) {
    this.productCodes = tags.map((t) => t.value) as any;
  }

  public handleFooterAction(action: CrmActionButton) {
    switch (action.id) {
      case FooterActionId.confirm:
        if (!this.isFormValid()) {
          this.messageService.error('messages.validation.invalidForm');
          return;
        }
        this.onSubmit?.(this);
        break;
      case FooterActionId.cancel:
        this.onCancel?.();
        break;
      default:
        break;
    }
  }

  public handleFileChanged(file: File | string) {
    if (!file) {
      this.existingCard = undefined;
      this.file = undefined;
    }
    if (typeof file === 'string') {
      this.existingCard = file;
      return;
    }
    this.file = file;
  }

  private isFormValid() {
    const isFileOrExisting = !!this.file || !!this.existingCard;
    if (this.multiProduct) {
      return !!this.productCodes?.length && isFileOrExisting;
    }
    return isFileOrExisting;
  }
}
